/**
 * Admin → AI Providers — typed api-client calls + TanStack Query hooks
 * (E05-F07).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). The transport uses
 * the shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) and `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` so the SPA speaks the exact same
 * shapes as the Fastify API.
 *
 * Endpoints (routes/admin-ai-providers.ts, admin-only):
 *   GET /admin/ai-providers → useAiProviders()       (read masked settings)
 *   PUT /admin/ai-providers → useUpdateAiProviders()  (full replace of both slots)
 *   GET /admin/ai-providers/models → useAiProviderModels() (cache-only, page load)
 *   GET /admin/ai-providers/models?refresh=1 → useRefreshAiProviderModels()
 *     ("Get actual models" button — always a live per-provider fetch)
 *
 * SECURITY: the masked tail (`maskedKey`) is NEVER sent back as `apiKey`. The PUT
 * body's `apiKey` is tri-state — omitted (keep stored key), '' (clear → env
 * fallback), or a freshly typed value (set). See {@link slotUpdateFromForm}.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  aiProviderSettingsResponseSchema,
  aiProviderModelsResponseSchema,
  type AiProviderSettingsResponse,
  type AiProviderSettingsUpdate,
  type AiProviderSlotUpdate,
  type AiProviderModelResult,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** Feature-local query key (kept out of the shared registry). */
export const AI_PROVIDERS_QUERY_KEY = ['admin', 'ai-providers'] as const;

/** The legacy command tag carried on every request (mirrors sibling admin tabs). */
const AI_PROVIDERS_COMMAND = { command: 'manage_ai_providers' } as const;

/** Fetch the masked AI-provider settings (both slots, always present). */
async function fetchAiProviders(): Promise<AiProviderSettingsResponse> {
  const result = await apiClient.get<unknown>('/admin/ai-providers', AI_PROVIDERS_COMMAND);
  return aiProviderSettingsResponseSchema.parse(unwrap(result));
}

/** Full-replace BOTH slots (PUT). The freshly-saved masked state comes back. */
async function putAiProviders(body: AiProviderSettingsUpdate): Promise<AiProviderSettingsResponse> {
  const result = await apiClient.put<unknown>('/admin/ai-providers', body, AI_PROVIDERS_COMMAND);
  return aiProviderSettingsResponseSchema.parse(unwrap(result));
}

/** Read query — the two slot editors. */
export function useAiProviders(): UseQueryResult<AiProviderSettingsResponse, ApiResultError> {
  return useQuery<AiProviderSettingsResponse, ApiResultError>({
    queryKey: AI_PROVIDERS_QUERY_KEY,
    queryFn: fetchAiProviders,
  });
}

/** Update mutation — invalidates the query so the re-read masked state refreshes. */
export function useUpdateAiProviders(): UseMutationResult<
  AiProviderSettingsResponse,
  ApiResultError,
  AiProviderSettingsUpdate
> {
  const queryClient = useQueryClient();
  return useMutation<AiProviderSettingsResponse, ApiResultError, AiProviderSettingsUpdate>({
    mutationFn: putAiProviders,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AI_PROVIDERS_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// "Get actual models" (2026-09-03 follow-up) — live per-provider model lists.
// ---------------------------------------------------------------------------

export const AI_PROVIDER_MODELS_QUERY_KEY = ['admin', 'ai-providers', 'models'] as const;

async function fetchAiProviderModels(refresh: boolean): Promise<AiProviderModelResult[]> {
  const path = refresh ? '/admin/ai-providers/models?refresh=1' : '/admin/ai-providers/models';
  const result = await apiClient.get<unknown>(path, AI_PROVIDERS_COMMAND);
  return aiProviderModelsResponseSchema.parse(unwrap(result)).results;
}

/**
 * Passive (cache-only) read — fetch on mount to pick up a still-fresh
 * server-side cache from an earlier refresh (see services/aiProviderModels.ts's
 * TTL). NEVER triggers a live provider call by itself; a provider with no
 * fresh cache entry is simply absent from the result, and the caller falls
 * back to the static `AI_PROVIDER_MODELS` list for it.
 */
export function useAiProviderModels(): UseQueryResult<AiProviderModelResult[], ApiResultError> {
  return useQuery<AiProviderModelResult[], ApiResultError>({
    queryKey: AI_PROVIDER_MODELS_QUERY_KEY,
    queryFn: () => fetchAiProviderModels(false),
  });
}

/**
 * The "Get actual models" button — always a live fetch per distinct
 * configured provider. Only the SUCCESSFUL entries merge into the passive
 * query's cache (a failed provider keeps whatever it already had — a prior
 * fetch, or nothing/static-fallback); the raw per-provider results (incl.
 * `ok:false` ones) are still returned to the caller so it can toast them.
 */
export function useRefreshAiProviderModels(): UseMutationResult<
  AiProviderModelResult[],
  ApiResultError,
  void
> {
  const queryClient = useQueryClient();
  return useMutation<AiProviderModelResult[], ApiResultError, void>({
    mutationFn: () => fetchAiProviderModels(true),
    onSuccess: (results) => {
      queryClient.setQueryData<AiProviderModelResult[]>(AI_PROVIDER_MODELS_QUERY_KEY, (prev) => {
        const byProvider = new Map((prev ?? []).map((r) => [r.provider, r]));
        for (const r of results) {
          if (r.ok) byProvider.set(r.provider, r);
        }
        return Array.from(byProvider.values());
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Body builder (pure — unit-tested through the component)
// ---------------------------------------------------------------------------

/** The tri-state intent for the API key field (drives the `apiKey` PUT field). */
export type ApiKeyAction =
  | { readonly kind: 'keep' } // omit apiKey → leave stored key unchanged
  | { readonly kind: 'clear' } // apiKey: '' → revert this slot to env fallback
  | { readonly kind: 'set'; readonly value: string }; // apiKey: '<new key>'

/**
 * Build ONE slot's PUT-update from its form intent.
 *  - `apiKey`: tri-state per {@link ApiKeyAction} — omitted (keep), '' (clear),
 *    or the typed value (set). The masked tail is NEVER passed here.
 *
 * NOTE: slots no longer carry a `model` override (Model-per-Task Matrix,
 * 2026-08-30 plan) — the effective model per task now lives on
 * `ai_prompts.primary_model` / `ai_prompts.fallback_model`, edited via the
 * matrix table (see `ModelMatrixTab.tsx` / `promptsApi.ts`).
 */
export function slotUpdateFromForm(args: {
  readonly provider: AiProviderSlotUpdate['provider'];
  readonly apiKeyAction: ApiKeyAction;
}): AiProviderSlotUpdate {
  const slot: AiProviderSlotUpdate = { provider: args.provider };
  if (args.apiKeyAction.kind === 'clear') {
    slot.apiKey = '';
  } else if (args.apiKeyAction.kind === 'set') {
    slot.apiKey = args.apiKeyAction.value;
  }
  // kind === 'keep' → apiKey omitted entirely (stored key untouched).
  return slot;
}
